import WorkspacePrimaryButton from "@/components/PrimaryButton";
import FilteredLessonsView from "@/components/FilteredLessonsView";
import FilterOptions from "@/components/FilterOptions";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ProgressFilter } from "@/components/ProgressChips";
import { sessionProgressCounts } from "./lessonProgressCounts";
import { isLessonVisible } from "@/constants/lessons";
import { useAuth, getDisplayName } from "@/hooks/useAuth";
import { Lesson } from "@/types/tutor";
import api from "@/utils/axios-utils";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AssessmentStatuses } from "./AssessmentsDropdown";
import LessonCard from "./LessonCard";
import StudentWelcome from "./StudentWelcome";
import StudentLessonScheduleView from "./StudentLessonScheduleView";
import { useStudentLessonScheduleEnabled } from "@/hooks/useRuntimeFlags";

type LessonWithStatus = Lesson & {
  status?: ProgressFilter;
  goalsMet?: number;
  totalGoals?: number;
  assignedAt?: string;
  finishedAt?: number | null;
  startedAt?: number | null;
  classroomId?: number | null;
};

const LegacyStudentLessonsView = () => {
  const [lessons, setLessons] = useState<LessonWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { classroomId } = useParams<{ classroomId: string }>();
  const { user } = useAuth();
  const [progressCounts, setProgressCounts] = useState({
    mastered: 0,
    inProgress: 0,
    notStarted: 0,
  });
  const [totalTimeMs, setTotalTimeMs] = useState(0);
  const [currentProgressFilter, setCurrentProgressFilter] =
    useState<ProgressFilter>("all");
  const [loadingLessonId, setLoadingLessonId] = useState<number | null>(null);
  const [assessmentStatusMap, setAssessmentStatusMap] = useState<
    Record<string, AssessmentStatuses>
  >({});

  const fetchAssessmentStatuses = async (lessonIds: number[]) => {
    if (!lessonIds.length) return;
    try {
      const { data } = await api.get<Record<string, AssessmentStatuses>>(
        `/exit-ticket/assessment-statuses`,
        { params: { lesson_ids: lessonIds.join(",") } },
      );
      setAssessmentStatusMap(data);
    } catch (err) {
      console.error("Error fetching assessment statuses:", err);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, [classroomId]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      setError(null);

      if (classroomId) {
        // Convert classroomId to number
        const classroomIdNum = parseInt(classroomId, 10);

        if (isNaN(classroomIdNum)) {
          setError("Invalid classroom ID");
          setLoading(false);
          return;
        }

        const [lessonsResponse, sessionsResponse] = await Promise.all([
          api.get(`/classrooms/${classroomIdNum}/lessons`),
          api.get(`/analytics/student/sessions`),
        ]);

        const sessions: Array<any> = sessionsResponse.data?.sessions || [];
        const byLessonAndClass: Record<string, any> = {};
        sessions.forEach((s) => {
          const key = `${Number(s.lesson_id)}_${Number(s.classroom_id || 0)}`;
          byLessonAndClass[key] = s;
        });

        const mapped: LessonWithStatus[] = (lessonsResponse.data || []).map(
          (l: Lesson) => {
            const s = byLessonAndClass[`${l.id}_${classroomIdNum}`];
            const raw = (s?.lesson_status || "").toString().toLowerCase();
            let status: ProgressFilter | undefined = undefined;
            if (raw === "complete" || raw === "completed") status = "mastered";
            else if (raw === "in_progress" || raw === "started")
              status = "in_progress";
            else if (raw === "not_started") status = "not_started";
            return {
              ...l,
              status,
              goalsMet: Number(s?.goals_met ?? 0),
              totalGoals: Number(s?.total_goals ?? 0),
              finishedAt: s?.session_end_ts ?? null,
              startedAt: s?.session_start_ts ?? null,
              classroomId: Number(s?.classroom_id ?? classroomIdNum),
            };
          },
        );

        const visible = mapped.filter((l) => isLessonVisible(l.id));
        setLessons(visible);
        fetchAssessmentStatuses(visible.map((l) => l.id));

        let totalTime = 0;
        sessions.forEach((s) => {
          if (s.classroom_id && s.classroom_id !== classroomIdNum) return;
          if (s.time_spent_ms) totalTime += Number(s.time_spent_ms);
        });
        setProgressCounts(
          sessionProgressCounts(
            sessions.filter(
              (s) => !s.classroom_id || s.classroom_id === classroomIdNum,
            ),
          ),
        );
        setTotalTimeMs(totalTime);
      } else {
        // Fetch all lessons if no classroom ID is provided
        const [lessonsResponse, sessionsResponse] = await Promise.all([
          api.get("/lessons/"),
          api.get(`/analytics/student/sessions`),
        ]);
        const sessions: Array<any> = sessionsResponse.data?.sessions || [];
        const byLessonId: Record<number, any> = {};
        sessions.forEach((s) => {
          byLessonId[Number(s.lesson_id)] = s;
        });
        const mapped: LessonWithStatus[] = (lessonsResponse.data || []).map(
          (l: Lesson) => {
            const s = byLessonId[l.id];
            const raw = (s?.lesson_status || "").toString().toLowerCase();
            let status: ProgressFilter | undefined = undefined;
            if (raw === "complete" || raw === "completed") status = "mastered";
            else if (raw === "in_progress" || raw === "started")
              status = "in_progress";
            else if (raw === "not_started") status = "not_started";
            return {
              ...l,
              status,
              goalsMet: Number(s?.goals_met ?? 0),
              totalGoals: Number(s?.total_goals ?? 0),
              finishedAt: s?.session_end_ts ?? null,
              startedAt: s?.session_start_ts ?? null,
              classroomId: Number(s?.classroom_id ?? 0),
            };
          },
        );
        const visible = mapped.filter((l) => isLessonVisible(l.id));
        setLessons(visible);
        fetchAssessmentStatuses(visible.map((l) => l.id));

        let totalTime = 0;
        sessions.forEach((s) => {
          if (s.time_spent_ms) totalTime += Number(s.time_spent_ms);
        });
        setProgressCounts(sessionProgressCounts(sessions));
        setTotalTimeMs(totalTime);
      }
    } catch (err) {
      console.error("Error fetching lessons:", err);
      setError("Failed to load lessons. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading lessons..." />;
  }

  if (error) {
    return (
      <div role="alert" className="et-group-error">
        <div className="text-center text-lg text-gray-500">{error}</div>
        <WorkspacePrimaryButton
          onClick={fetchLessons}
          className="et-primary-button"
        >
          Try Again
        </WorkspacePrimaryButton>
      </div>
    );
  }
  return (
    <div className="et-student-lessons">
      <StudentWelcome
        name={getDisplayName(user)}
        totalTimeMs={totalTimeMs}
        masteredCount={progressCounts.mastered}
        inProgressCount={progressCounts.inProgress}
        notStartedCount={progressCounts.notStarted}
        showSummary={Boolean(classroomId)}
      />
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <FilteredLessonsView
            lessons={lessons}
            initialFilterType="status_then_date"
            renderFilters={(onFiltersChange) => (
              <div className="flex flex-col gap-4">
                <h2 className="et-student-section-heading">
                  Your teacher assigned these lessons
                </h2>
                <div className="et-student-controls">
                  <div className="et-student-filter-row">
                    <div
                      className="et-progress-filters"
                      role="group"
                      aria-label="Filter lessons by progress"
                    >
                      {(
                        [
                          ["all", "All"],
                          ["mastered", "Completed"],
                          ["in_progress", "In Progress"],
                          ["not_started", "Not Started"],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          type="button"
                          key={value}
                          aria-pressed={currentProgressFilter === value}
                          onClick={() => {
                            setCurrentProgressFilter(value);
                            onFiltersChange({ statusFilter: value });
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="et-student-search-sort">
                      <FilterOptions
                        showSortLabel
                        searchPlaceholder="Search lesson"
                        onFiltersChange={onFiltersChange}
                        initialFilter="status_then_date"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            emptyState={
              <div className="flex flex-col gap-4 text-center text-2xl font-bold p-8">
                <h3 className="text-lightgreen">Oopsssss......</h3>
                <h3 className="text-black">
                  You have no lessons assigned right now. Check back soon!
                </h3>
              </div>
            }
          >
            {(filteredLessons) => (
              <div className="et-student-list">
                {filteredLessons.map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    classroomId={classroomId || ""}
                    globalLoadingLessonId={loadingLessonId}
                    onStartStateChange={(loading, lessonId) => {
                      setLoadingLessonId(loading ? lessonId : null);
                    }}
                    assessmentStatuses={assessmentStatusMap[String(lesson.id)]}
                  />
                ))}
              </div>
            )}
          </FilteredLessonsView>
        </div>
      </div>
    </div>
  );
};

const StudentLessonsView = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const studentLessonScheduleEnabled = useStudentLessonScheduleEnabled();

  if (studentLessonScheduleEnabled && classroomId) {
    return (
      <StudentLessonScheduleView
        classroomId={classroomId}
        legacyFallback={<LegacyStudentLessonsView />}
      />
    );
  }

  return <LegacyStudentLessonsView />;
};

export default StudentLessonsView;
